import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string; // Full name or email as fallback
  isGoogleAccount: boolean;
  role?: 'admin' | 'manager' | 'user'; // Legacy role, now workspace-specific
  isPortalAdmin?: boolean;
}

interface WorkspaceSelection {
  requiresWorkspaceSelection: boolean;
  workspaces: Array<{ id: string; slug: string; name: string; workspaceRole?: string }>;
  user: User;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<WorkspaceSelection | null>;
  loginWithToken: (token: string) => Promise<WorkspaceSelection | null>;
  selectWorkspace: (workspaceId: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      // Check if token requires workspace selection before fetching user
      try {
        const tokenData = JSON.parse(atob(storedToken.split('.')[1]));
        // If user has multiple workspaces but no selected workspace, don't fetch user yet
        if (tokenData.workspaceIds && Array.isArray(tokenData.workspaceIds) && tokenData.workspaceIds.length > 1 && !tokenData.workspaceId) {
          // Don't set token or fetch user - wait for workspace selection
          setLoading(false);
          return;
        }
      } catch (e) {
        // Token parsing failed, continue with normal flow
      }
      
      setToken(storedToken);
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (authToken: string) => {
    try {
      // First check if token requires workspace selection
      try {
        const tokenData = JSON.parse(atob(authToken.split('.')[1]));
        // If user has multiple workspaces but no selected workspace, don't set user
        if (tokenData.workspaceIds && Array.isArray(tokenData.workspaceIds) && tokenData.workspaceIds.length > 1 && !tokenData.workspaceId) {
          // Don't set user - workspace selection is required
          setLoading(false);
          return;
        }
      } catch (e) {
        // Token parsing failed, continue with normal flow
      }
      
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Non-JSON response from /api/auth/me:', text.substring(0, 200));
          throw new Error('Server returned invalid response format');
        }
        const userData = await response.json();
        
        // Double-check: if user has multiple workspaces but no workspace selected, don't set user
        if (userData.workspaces && Array.isArray(userData.workspaces) && userData.workspaces.length > 1) {
          const token = localStorage.getItem('authToken');
          if (token) {
            try {
              const tokenData = JSON.parse(atob(token.split('.')[1]));
              if (!tokenData.workspaceId) {
                // Workspace selection required - don't set user
                setLoading(false);
                return;
              }
            } catch (e) {
              // Token parsing failed, continue
            }
          }
        }
        
        setUser(userData);
        setLoading(false);
      } else {
        // Check if error response is JSON
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            console.error('Error from /api/auth/me:', errorData.error || 'Unauthorized');
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const text = await response.text();
          console.error('Non-JSON error response from /api/auth/me:', text.substring(0, 200));
        }
        localStorage.removeItem('authToken');
        setToken(null);
        setUser(null);
        setLoading(false);
        // Don't throw error - just clear auth state
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('authToken');
      setToken(null);
      setUser(null);
      setLoading(false);
      // Don't throw error - just clear auth state
    }
  };

  const login = async (email: string, password: string): Promise<WorkspaceSelection | null> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        // Check if response is JSON before trying to parse
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Login failed';
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const error = await response.json();
            errorMessage = error.error || 'Login failed';
          } catch (parseError) {
            // If JSON parsing fails, use status text
            errorMessage = response.statusText || `Server error (${response.status})`;
          }
        } else {
          // Response is not JSON (likely HTML error page)
          const text = await response.text();
          console.error('Non-JSON error response:', text.substring(0, 200));
          if (text.includes('Proxy error') || text.includes('proxy')) {
            errorMessage = 'Server proxy error. Please check server configuration.';
          } else {
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      // Check if successful response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON success response:', text.substring(0, 200));
        throw new Error('Server returned invalid response format');
      }

      const data = await response.json();
      
      // Check if workspace selection is required
      if (data.requiresWorkspaceSelection) {
        // Don't set user yet - wait for workspace selection
        // Setting user would trigger isAuthenticated and cause redirect
        // Store temp token for workspace selection but don't set user/auth state
        if (data.tempToken) {
          localStorage.setItem('authToken', data.tempToken);
        }
        return {
          requiresWorkspaceSelection: true,
          workspaces: data.workspaces,
          user: data.user
        };
      }
      
      // Single workspace, auto-selected, or portal admin
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('authToken', data.token);
      if (data.workspace) {
        localStorage.setItem('currentWorkspaceId', data.workspace.id);
        localStorage.setItem('currentWorkspace', JSON.stringify(data.workspace));
      } else if (data.portal) {
        // Portal admin - clear workspace context
        localStorage.removeItem('currentWorkspaceId');
        localStorage.removeItem('currentWorkspace');
      }
      return null;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const selectWorkspace = async (workspaceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch('/api/auth/select-workspace', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ workspaceId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to select workspace');
      }

      const data = await response.json();
      setToken(data.token);
      localStorage.setItem('authToken', data.token);
      if (data.workspace) {
        localStorage.setItem('currentWorkspaceId', data.workspace.id);
        localStorage.setItem('currentWorkspace', JSON.stringify(data.workspace));
      }
      
      // Now fetch and set the user to complete authentication
      // This will set isAuthenticated to true
      await fetchUser(data.token);
    } catch (error) {
      console.error('Workspace selection error:', error);
      throw error;
    }
  };

  const loginWithToken = async (authToken: string): Promise<WorkspaceSelection | null> => {
    setToken(authToken);
    localStorage.setItem('authToken', authToken);
    
    // Decode token to check if workspace selection is needed
    try {
      const tokenData = JSON.parse(atob(authToken.split('.')[1]));
      
      // If token has a workspaceId (single workspace auto-selected), set it in localStorage
      if (tokenData.workspaceId && typeof tokenData.workspaceId === 'string') {
        // Fetch workspace details to store in localStorage
        try {
          const userData = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          }).then(res => res.json());
          
          if (userData.workspaces && Array.isArray(userData.workspaces)) {
            const workspace = userData.workspaces.find((w: any) => w.id === tokenData.workspaceId);
            if (workspace) {
              localStorage.setItem('currentWorkspaceId', workspace.id);
              localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
            }
          }
        } catch (error) {
          console.error('Error fetching workspace details:', error);
          // Still set workspaceId even if we can't fetch details
          localStorage.setItem('currentWorkspaceId', tokenData.workspaceId);
        }
      } else if (tokenData.isPortalAdmin) {
        // Portal admin - clear workspace context
        localStorage.removeItem('currentWorkspaceId');
        localStorage.removeItem('currentWorkspace');
      }
      
      // Check if user has multiple workspaces but no selected workspace
      if (tokenData.workspaceIds && Array.isArray(tokenData.workspaceIds) && tokenData.workspaceIds.length > 1 && !tokenData.workspaceId) {
        // User needs to select a workspace
        // Fetch user's workspaces from the API
        const userData = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }).then(res => res.json());
        
        if (userData.workspaces && userData.workspaces.length > 1) {
          // Don't set user yet - wait for workspace selection
          // Setting user would trigger isAuthenticated and cause redirect
          const userObj: User = {
            id: userData.id,
            email: userData.email,
            isGoogleAccount: userData.isGoogleAccount || false,
            isPortalAdmin: userData.isPortalAdmin || false
          };
          
          // Don't call setUser here - it will be set after workspace selection
          
          return {
            requiresWorkspaceSelection: true,
            workspaces: userData.workspaces.map((w: any) => ({
              id: w.id,
              slug: w.slug,
              name: w.name,
              workspaceRole: w.role
            })),
            user: userObj
          };
        }
      }
      
      // Single workspace or already selected, proceed with normal login
      await fetchUser(authToken);
      return null;
    } catch (error) {
      console.error('Error in loginWithToken:', error);
      // Fallback to normal login if token parsing fails
      await fetchUser(authToken);
      return null;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    // Clear workspace context on logout to prevent cross-user workspace access
    localStorage.removeItem('currentWorkspaceId');
    localStorage.removeItem('currentWorkspace');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    loginWithToken,
    selectWorkspace,
    logout,
    isAuthenticated: !!user && !!token,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

